<template>
    <ValidationProvider :name="label"
                        :vid="name"
                        :rules="rules">
        <div v-if="!isLoading && !removeCurrentFile && currentFile && (this.multiple || rawFiles.length === 0)">
            <slot name="currentfile"></slot>
            <v-icon size="18"
                        @click="setRemoveCurrentFile()"
                        class="clickable ml-4">{{ ICONS.DELETE }}</v-icon>
        </div>
        <div v-if="convertedFiles.length > 0">
            <v-layout v-for="(item, i) in convertedFiles"
                      class="mt-2"
                      :key="i">
                <a @click="showDocument(item.content)"
                   target="_blank">{{ item.name }}</a>
                <v-icon size="18"
                        @click="removeFile(item)"
                        class="clickable ml-4">{{ ICONS.DELETE }}</v-icon>
            </v-layout>
        </div>
        <div :class="className">
            <span class="d-inline-block">{{ label }}</span>
            <file-upload v-model="rawFiles"
                         ref="upload"
                         :accept="accept"
                         :input-id="inputId"
                         :multiple="multiple"
                         @input-filter="inputFilterQuestion">
                <v-btn color="primary" outlined :disabled="isLoading">
                    <slot name="icon">
                        <v-icon size="20" class="mr-2">{{ ICONS.FILE }}</v-icon>
                    </slot>
                    {{ title }}</v-btn>
            </file-upload>
        </div>
        <p class="mt-2 text--xxs"
           v-if="accept">{{ acceptedFilesNote }}</p>
        <p v-if="validationError"
           class="text--xs font-weight-medium red--text">{{ $t('validation.file_type_error') }}</p>

        <ConfirmationDialog :dialog="showChangeAttachmentConfirmation"
            :text="changeFileQuestion"
            :action-button-title="$t('labels.continue')"
            :action-loader="loader"
            @confirm="inputFilter($event)"></ConfirmationDialog>
    </ValidationProvider>
</template>

<script>
import { ValidationProvider } from 'vee-validate';
import { ICONS } from '@/constants/icons';
import isFieldRequired from '@/mixins/isFieldRequired';
import { showBase64Document, dataURLtoFile } from '@/helpers/files';
import ConfirmationDialog from '@/components/widgets/dialogs/ConfirmationDialog';

export default {
    name: 'FileInputMultiPart',
    components: { ValidationProvider, ConfirmationDialog },
    mixins: [isFieldRequired],
    props: {
        name: {
            type: String,
            'default': ''
        },
        currentFile: {
            type: String,
            'default': ''
        },
        changeFileQuestion: {
            type: String,
            'default': ''
        },
        value: {
            type: Array,
            required: true
        },
        inputId: {
            type: String,
            required: true
        },
        label: {
            type: String,
            'default': ''
        },
        rules: {
            type: [String, Object, Array],
            'default': ''
        },
        multiple: {
            type: Boolean,
            'default': false
        },
        formVisibility: {
            type: Boolean,
            'default': false
        },
        accept: {
            type: String,
            'default': ''
        },
        className: {
            type: [String, Object, Array],
            'default': ''
        },
        buttonTitle: {
            type: String,
            'default': ''
        },
        isLoading: {
            type: Boolean,
            'default': 'false'
        }
    },
    data: () => ({
        ICONS,
        convertedFiles: [],
        rawFiles: [],
        validationError: false,
        showChangeAttachmentConfirmation: false,
        loader: false,
        removeCurrentFile: false
    }),
    computed: {
        title () {
            return this.buttonTitle || this.$t('buttons.add');
        },
        acceptedFilesNote () {
            const typesAliases = {
                xlsx: 'vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            };

            if (this.accept) {
                const regex = new RegExp('\\w+\\/', 'gi');
                let types = this.accept.replaceAll(regex, ' .');
                if (types.includes(typesAliases.xlsx)) {
                    types = types.replace(typesAliases.xlsx, 'xlsx');
                }
                return `${this.$t('messages.accepted_files')}: ${types}`;
            } else {
                return '';
            }
        }
    },
    watch: {
        rawFiles: {
            handler (val) {
                const data = [...val];
                data.forEach(el => {
                    if (!this.accept.includes(el.type) && !this.accept.includes(el.file.type)) {
                        this.removeFile(el);
                        this.validationError = true;
                    }
                });

                this.convertedFiles = data.map(el => el.file);
                this.$emit('input', this.convertedFiles);
            }
        },
        formVisibility: {
            handler (val) {
                if (val) {
                    setTimeout(() => {
                        this.value.forEach(f => {
                            this.rawFiles.push({
                                file: dataURLtoFile(f.content, f.name),
                                name: f.name
                            });
                        });
                    }, 150);
                }
            },
            immediate: true
        }
    },
    methods: {
        inputFilterQuestion (newFile) {
            if (this.currentFile && !this.multiple && this.changeFileQuestion) {
                this.showChangeAttachmentConfirmation = true;
            } else {
                this.inputFilter(true);
            }
        },
        inputFilter (event) {
            if (event) {
                this.validationError = false;
                this.showChangeAttachmentConfirmation = false;
            } else {
                this.showChangeAttachmentConfirmation = false;
                this.rawFiles = [];
            }
        },
        removeFile (item) {
            if (this.multiple) {
                this.$refs.upload.remove(item);
            } else {
                this.$refs.upload.clear();
            }
        },
        setRemoveCurrentFile () {
            if (!this.removeCurrentFile) {
                this.$emit('remove-current-file', null);
                this.removeCurrentFile = true;
            }
        },
        showDocument (content) {
            showBase64Document(content);
        },
        clearFiles () {
            this.rawFiles = [];
            this.convertedFiles = [];
        }
    }
};
</script>

<style scoped lang="scss">
    ::v-deep {
        & .v-input__prepend-inner {
            margin-top: 6px;
            & .v-icon {
                font-size: 20px;
            }
        }
        & .v-input__append-inner {
            & .v-icon {
                font-size: 20px;
            }
        }
    }
</style>
